import { store } from '../redux/store';
import { changeLocale } from '../redux/actions';
import { getLocaleWithCountryCode, getLocaleWithReasonableFallback, LocaleCode, useAutoLanguageChange } from './language';

/**
 * Sets up an observer to watch for changes to the HTML lang attribute
 * and updates translations when it changes, but only if RTR_LOCALE is not set
 */
export function setupLangObserver(): void {
  // Only set up the observer if RTR_LOCALE is not set
  if (!useAutoLanguageChange()) {
    return;
  }

  // Create a component-like object to map dispatch to
  // @ts-ignore - We know changeLocale will be added by mapDispatchToProps
  const dispatchContainer: { changeLocale: (locale: LocaleCode) => void } = {};
  store.mapDispatchToProps(dispatchContainer, {
    changeLocale,
  });

  const htmlElement = document.documentElement;
  let currentLang = '';

  /**
   * Process language changes and dispatch only if necessary
   * @param newHtmlLang The raw HTML lang attribute value
   */
  const processLanguageChange = (newHtmlLang: string): void => {
    // Get the formatted locale with country code
    const newLang = getLocaleWithCountryCode(newHtmlLang);

    // Skip if the new language is empty or unchanged
    if (!newLang || newLang === currentLang) {
      return;
    }

    // Get valid languages from store
    const state = store.getState();
    const systemLanguages = state?.system?.languages || [];
    const systemDefaultLanguage = state?.system?.defaultLanguage || '';

    // Skip if we don't have valid languages yet
    if (systemLanguages.length === 0) {
      return;
    }

    // Determine the locale to use (either valid new locale or fallback)
    const localeToUse = getLocaleWithReasonableFallback(newLang, systemLanguages, systemDefaultLanguage);

    // Only dispatch if the locale is different from current
    if (localeToUse !== currentLang) {
      dispatchContainer.changeLocale(localeToUse);
      currentLang = localeToUse; // Update current language after change
    }
  };

  // Set up a mutation observer to watch for changes to the lang attribute
  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'lang' && htmlElement.lang !== currentLang) {
        processLanguageChange(htmlElement.lang);
        break; // Only need to process once if multiple mutations affect lang
      }
    }
  });

  // Start observing the HTML element for attribute changes
  observer.observe(htmlElement, { attributes: true, attributeFilter: ['lang'] });

  // Process initial language if present
  if (htmlElement.lang) {
    processLanguageChange(htmlElement.lang);
  }
}
